import React, { useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

// Date formatting helper function
const formatDate = (dateString) => {
  if (!dateString) return '';

  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString; // Return original if invalid date

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();

  return `${day}-${month}-${year}`;
};

const getTodayDate = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const CheckoutDetails = () => {
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [guestData, setGuestData] = useState(null);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [disclaimerData, setDisclaimerData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const navigate = useNavigate();

  // Get guest ID from JWT token
  const getGuestIdFromToken = (token) => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.sub;
    } catch (e) {
      console.error('Error parsing token:', e);
      return null;
    }
  };

  // Decrypt user data
  const decryptUserData = async (encryptedData) => {
    try {
      if (encryptedData.email && encryptedData.email.length < 50) {
        return encryptedData;
      }

      const accessToken = Cookies.get('access_token');
      if (!accessToken) {
        throw new Error('No access token found');
      }

      const response = await fetch(`${BQ_BASE_URL}/bq/api/decrypt-fields`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          encrypted_fields: {
            email: encryptedData.email,
            phone_number: encryptedData.phone_number,
            country_code: encryptedData.country_code,
          },
        }),
      });

      if (response.status === 401) {
        return encryptedData;
      }

      if (!response.ok) {
        throw new Error(`Decryption failed: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Decryption error:', error);
      return encryptedData;
    }
  };

  // Fetch guest data
  const getGuestData = async () => {
    const accessToken = Cookies.get('access_token');
    if (!accessToken) return null;

    try {
      const guestId = Cookies.get('guest_id') || getGuestIdFromToken(accessToken);
      if (!guestId) return null;

      const profileResponse = await fetch(`${BQ_BASE_URL}/bq/api/profile?guest_id=${guestId}`, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!profileResponse.ok) {
        throw new Error(`Profile API Error: ${profileResponse.statusText}`);
      }

      const profileData = await profileResponse.json();
      const isDecrypted = profileData.emailid && profileData.emailid.length < 50;

      if (isDecrypted) {
        return {
          firstname: profileData.firstname || '',
          lastname: profileData.lastname || '',
          email: profileData.emailid || '',
          phone: profileData.phonenumber || '',
          countrycode: profileData.countrycode || '',
        };
      }

      const decryptedFields = await decryptUserData({
        email: profileData.emailid,
        phone_number: profileData.phonenumber,
        country_code: profileData.countrycode,
      });

      return {
        firstname: profileData.firstname || '',
        lastname: profileData.lastname || '',
        email: decryptedFields.email || profileData.emailid || '',
        phone: decryptedFields.phone_number || profileData.phonenumber || '',
        countrycode: decryptedFields.country_code || profileData.countrycode || '',
      };
    } catch (error) {
      console.error('Error fetching guest data:', error);
      return null;
    }
  };

  // Fetch valid checkout list using phone or email
  const fetchCheckoutValidList = async (guestData) => {
    try {
      const accessToken = Cookies.get('access_token');
      if (!accessToken) {
        throw new Error('No access token found');
      }

      // Build URL based on available identifiers (phone takes precedence)
      let url;
      if (guestData.phone) {
        // Use phone number as primary identifier
        url = `${BQ_BASE_URL}/bq/api/checkout-valid-list?phone=${encodeURIComponent(guestData.phone)}`;
        console.log('Fetching reservations using phone:', guestData.phone);
      } else if (guestData.email) {
        // Fall back to email if phone is not available
        url = `${BQ_BASE_URL}/bq/api/checkout-valid-list?email=${encodeURIComponent(guestData.email)}`;
        console.log('Fetching reservations using email:', guestData.email);
      } else {
        throw new Error('No valid identifier (phone or email) available');
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Checkout valid list API Error: ${response.statusText}`);
      }

      const data = await response.json();

      // Check if the API returned success and has reservations
      if (data.success && data.reservations) {
        return data.reservations;
      } else if (data.available_reservations === false) {
        // No reservations found
        return [];
      } else {
        // Return empty array if no reservations
        return [];
      }
    } catch (error) {
      console.error('Error fetching checkout valid list:', error);
      throw error;
    }
  };

  // Calculate checkout status
  const calculateCheckoutStatus = (checkoutDate, isEarlyCheckout, isLateCheckout) => {
    // Ensure we have a valid date
    if (!checkoutDate) {
      return {
        type: 'unknown',
        status: 'error',
        message: 'Checkout date not available',
        chargeApplied: false,
      };
    }

    const now = new Date();
    let checkoutDateTime;

    // Handle different date formats from API
    if (typeof checkoutDate === 'string') {
      checkoutDateTime = new Date(checkoutDate);
    } else if (checkoutDate instanceof Date) {
      checkoutDateTime = new Date(checkoutDate);
    } else {
      // If it's an object with date properties
      checkoutDateTime = new Date(checkoutDate.date || checkoutDate);
    }

    // Validate the date
    if (isNaN(checkoutDateTime.getTime())) {
      return {
        type: 'unknown',
        status: 'error',
        message: 'Invalid checkout date format',
        chargeApplied: false,
      };
    }

    // Set checkout time to 1 PM (hotel standard checkout time)
    checkoutDateTime.setHours(11, 0, 0, 0);

    // Check if it's early checkout (before scheduled checkout day)
    const isBeforeScheduledDay = now < new Date(checkoutDateTime.setHours(0, 0, 0, 0));

    if (isEarlyCheckout || isBeforeScheduledDay) {
      // Early checkout (before scheduled checkout day)
      if (now < checkoutDateTime) {
        return {
          type: 'early',
          status: 'no_charge',
          message: 'Early checkout before 1 PM - No additional charge',
          chargeApplied: false,
        };
      } else {
        return {
          type: 'early',
          status: 'extra_day_charge',
          message: 'Early checkout after 1 PM - Charged for an extra day',
          chargeApplied: true,
        };
      }
    }

    // Check if it's late checkout (on scheduled checkout day)
    if (isLateCheckout || !isBeforeScheduledDay) {
      const gracePeriodEnd = new Date(checkoutDateTime);
      gracePeriodEnd.setHours(15, 0, 0, 0); // 3 PM grace period end

      if (now <= gracePeriodEnd) {
        return {
          type: 'late',
          status: 'grace_period',
          message: 'Late checkout within grace period (until 3 PM) - No additional charge',
          chargeApplied: false,
        };
      } else {
        return {
          type: 'late',
          status: 'extra_day_charge',
          message: 'Late checkout after 3 PM - Charged for an extra day',
          chargeApplied: true,
        };
      }
    }

    // Default case (shouldn't normally reach here)
    return {
      type: 'normal',
      status: 'on_time',
      message: 'Checkout on time - No additional charge',
      chargeApplied: false,
    };
  };

  // Initialize data
  useEffect(() => {
    const initializeData = async () => {
      try {
        setLoading(true);
        const guest = await getGuestData();
        if (!guest) {
          throw new Error('Failed to fetch guest data');
        }
        setGuestData(guest);

        // Fetch reservations using phone (preferred) or email
        const reservations = await fetchCheckoutValidList(guest);
        setReservations(reservations);

        // Show success toast if reservations found
        // if (reservations.length > 0) {
        //   toast.success(`Found ${reservations.length} reservation(s) for checkout`, {
        //     position: "top-center",
        //     autoClose: 3000,
        //   });
        // }
      } catch (err) {
        setError(err.message);
        toast.error(err.message, {
          position: 'top-center',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
      } finally {
        setLoading(false);
      }
    };

    initializeData();
  }, []);

  const handleProceed = (bookingId, orderId, reservation) => {
    // Get today's date for desired_checkout_date
    const desiredCheckoutDate = getTodayDate();

    // Calculate checkout status
    const checkoutStatus = calculateCheckoutStatus(
      reservation.reservation_details.checkout_date,
      false, // isEarlyCheckout - you might need to adjust this based on your data
      false // isLateCheckout - you might need to adjust this based on your data
    );

    // Show disclaimer if there's a charge or it's an early checkout
    if (checkoutStatus.chargeApplied || checkoutStatus.type === 'early') {
      setSelectedReservation({
        bookingId,
        orderId,
        reservation,
        desiredCheckoutDate,
      });
      setDisclaimerData({
        checkoutStatus,
        reservationDetails: reservation.reservation_details,
      });
      setShowDisclaimer(true);
    } else {
      // Proceed directly if no disclaimer needed
      proceedToCheckoutVerification(bookingId, orderId, desiredCheckoutDate);
    }
  };

  const handleDisclaimerContinue = () => {
    if (selectedReservation) {
      proceedToCheckoutVerification(
        selectedReservation.bookingId,
        selectedReservation.orderId,
        selectedReservation.desiredCheckoutDate
      );
    }
    setShowDisclaimer(false);
  };

  const handleDisclaimerCancel = () => {
    setShowDisclaimer(false);
    setSelectedReservation(null);
    setDisclaimerData(null);
  };

  // Helper function to get identifier from various sources
  const getIdentifierFromUserData = () => {
    // Try to get from localStorage first (userDataCache)
    const userDataCache = localStorage.getItem('userDataCache');
    if (userDataCache) {
      try {
        const parsedCache = JSON.parse(userDataCache);
        // Try phone number first
        if (parsedCache.data && parsedCache.data.phone_number) {
          return {
            identifier: parsedCache.data.phone_number,
            countryCode: parsedCache.data.country_code || '',
            type: 'phone',
          };
        }
        // Then try email
        if (parsedCache.data && parsedCache.data.email) {
          return {
            identifier: parsedCache.data.email,
            countryCode: '',
            type: 'email',
          };
        }
      } catch (e) {
        console.error('Error parsing user cache:', e);
      }
    }

    // Fall back to guestData state
    if (guestData) {
      if (guestData.phone) {
        return {
          identifier: guestData.phone,
          countryCode: guestData.countrycode || '',
          type: 'phone',
        };
      }
      if (guestData.email) {
        return {
          identifier: guestData.email,
          countryCode: '',
          type: 'email',
        };
      }
    }

    return null;
  };

  const proceedToCheckoutVerification = async (bookingId, orderId, desiredCheckoutDate) => {
    try {
      setIsLoading(true);
      const accessToken = Cookies.get('access_token');
      if (!accessToken) {
        throw new Error('No access token found');
      }

      // Get identifier from user data
      const userIdentifier = getIdentifierFromUserData();
      if (!userIdentifier) {
        throw new Error('No identifier (phone or email) found for checkout');
      }

      console.log(`Using ${userIdentifier.type} for checkout:`, userIdentifier.identifier);

      // Build URL based on identifier type
      let url;
      if (userIdentifier.type === 'phone' && userIdentifier.countryCode) {
        // Phone number with country code
        url = `${BQ_BASE_URL}/bq/api/check-out-verification?orderid=${orderId}&identifier=${encodeURIComponent(userIdentifier.identifier)}&countrycode=${encodeURIComponent(userIdentifier.countryCode)}&desired_checkout_date=${desiredCheckoutDate}`;
      } else {
        // Email or phone without country code
        url = `${BQ_BASE_URL}/bq/api/check-out-verification?orderid=${orderId}&identifier=${encodeURIComponent(userIdentifier.identifier)}&desired_checkout_date=${desiredCheckoutDate}`;
      }

      console.log('Calling checkout verification with URL:', url);

      // Call the checkout verification API (GET)
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          accept: 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        if (errorData.detail) {
          const { message, details } = errorData.detail;
          let errorMessage = message;

          if (details) {
            errorMessage += ` (${details.code})`;
            if (details.resolution) {
              errorMessage += ` - ${details.resolution}`;
            }
          }

          throw new Error(errorMessage);
        }
        throw new Error(`Checkout verification failed: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.success) {
        // After successful verification, call the POST API
        await proceedToCheckout(bookingId, orderId, desiredCheckoutDate, data);
      } else {
        throw new Error(data.message || 'Checkout verification failed');
      }
    } catch (error) {
      console.error('Error during checkout verification:', error);
      toast.error(error.message, {
        position: 'top-center',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const proceedToCheckout = async (bookingId, orderId, desiredCheckoutDate, verificationData) => {
    try {
      setIsLoading(true);
      const accessToken = Cookies.get('access_token');
      if (!accessToken) {
        throw new Error('No access token found');
      }

      // Get identifier from user data
      const userIdentifier = getIdentifierFromUserData();
      if (!userIdentifier) {
        throw new Error('No identifier (phone or email) found for checkout');
      }

      console.log(`Using ${userIdentifier.type} for checkout POST:`, userIdentifier.identifier);

      // Build URL based on identifier type
      let url;
      if (userIdentifier.type === 'phone' && userIdentifier.countryCode) {
        // Phone number with country code
        url = `${BQ_BASE_URL}/bq/api/check-out-verification?orderid=${orderId}&identifier=${encodeURIComponent(userIdentifier.identifier)}&countrycode=${encodeURIComponent(userIdentifier.countryCode)}&desired_checkout_date=${desiredCheckoutDate}`;
      } else {
        // Email or phone without country code
        url = `${BQ_BASE_URL}/bq/api/check-out-verification?orderid=${orderId}&identifier=${encodeURIComponent(userIdentifier.identifier)}&desired_checkout_date=${desiredCheckoutDate}`;
      }

      console.log('Calling checkout POST with URL:', url);

      // Call the checkout POST API
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          accept: 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        const errorData = await response.json();
        if (errorData.detail) {
          const { message, details } = errorData.detail;
          let errorMessage = message;

          if (details) {
            errorMessage += ` (${details.code})`;
            if (details.resolution) {
              errorMessage += ` - ${details.resolution}`;
            }
          }

          throw new Error(errorMessage);
        }
        throw new Error(`Checkout failed: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.success) {
        // Store the checkout data in localStorage
        localStorage.setItem('checkoutData', JSON.stringify(data));

        // Store individual values for easy access
        localStorage.setItem('orderId', data.orderid);
        localStorage.setItem('bookingId', data.bookingid);
        localStorage.setItem('earlyCheckout', data.early_checkout);
        localStorage.setItem('reservationDetails', JSON.stringify(data.reservation_details));
        localStorage.setItem('primaryGuest', JSON.stringify(data.primary_guest));

        // Navigate to booking checkin page
        navigate('/booking-checkin');
      } else {
        throw new Error(data.message || 'Checkout failed');
      }
    } catch (error) {
      console.error('Error during checkout:', error);
      toast.error(error.message, {
        position: 'top-center',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-2xl font-semibold text-gray-800">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-2xl font-semibold text-red-600">{error}</div>
      </div>
    );
  }

  return (
    <div className="min-h-1 mb-44 pb-24">
      {/* Main Content */}
      <main className="mx-auto">
        {reservations.length === 0 ? (
          <div className="flex justify-center mt-40">
            <p className="text-black font-bold text-2xl">No reservations available for checkout</p>
          </div>
        ) : (
          <div className="bg-white shadow-sm rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      scope="col"
                      className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Room Details
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Check-in
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Check-out
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Nights
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Guests
                    </th>
                    <th
                      scope="col"
                      className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {reservations.map((reservation) => (
                    <tr key={reservation.bookingid} className="hover:bg-gray-50">
                      <td className="px-3 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          Room {reservation.reservation_details.room_number}
                        </div>
                        <div className="text-sm text-gray-500">
                          {reservation.reservation_details.room_type}
                        </div>
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatDate(reservation.reservation_details.checkin_date)}
                        </div>
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatDate(reservation.reservation_details.checkout_date)}
                        </div>
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {reservation.reservation_details.length_of_stay}
                        </div>
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {reservation.reservation_details.number_of_guests}
                        </div>
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() =>
                            handleProceed(reservation.bookingid, reservation.orderid, reservation)
                          }
                          className="bg-black text-white px-4 py-2 rounded hover:bg-gray-800 text-xs"
                        >
                          Proceed to Checkout
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>

      {/* Disclaimer Popup */}
      {showDisclaimer && disclaimerData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-lg w-full p-7">
            <h2 className="text-xl font-bold mb-4">
              {disclaimerData.checkoutStatus.type === 'early'
                ? 'Early Checkout Notice'
                : 'Late Checkout Notice'}
            </h2>

            {disclaimerData.checkoutStatus.type === 'early' &&
              !disclaimerData.checkoutStatus.chargeApplied && (
                <p className="mb-4">
                  You are trying to check out earlier than your scheduled check-out date of{' '}
                  <strong>{formatDate(disclaimerData.reservationDetails.checkout_date)}</strong>.
                  Are you sure you want to continue?
                </p>
              )}

            {disclaimerData.checkoutStatus.type === 'early' &&
              disclaimerData.checkoutStatus.chargeApplied && (
                <p className="mb-4">
                  You are checking out early instead of your scheduled check-out on{' '}
                  <strong>{formatDate(disclaimerData.reservationDetails.checkout_date)}</strong>.
                  Since you are leaving after 1 PM, it will be considered as an additional day, and
                  you will incur a charge of one extra day .
                </p>
              )}

            {disclaimerData.checkoutStatus.type === 'late' &&
              !disclaimerData.checkoutStatus.chargeApplied && (
                <p className="mb-4">
                  You are checking out late but within the grace period (until 3 PM). No additional
                  charges will be applied.
                </p>
              )}

            {disclaimerData.checkoutStatus.type === 'late' &&
              disclaimerData.checkoutStatus.chargeApplied && (
                <p className="mb-4">
                  Since your check-out was after <strong>3:00 PM</strong>, an additional charge
                  equivalent to one extra day has been applied .
                </p>
              )}

            <div className="flex justify-end space-x-4 mt-10">
              <button
                onClick={handleDisclaimerCancel}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleDisclaimerContinue}
                disabled={isLoading}
                className={`px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 ${
                  isLoading ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {isLoading ? 'Processing...' : 'Proceed'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CheckoutDetails;

// import React, { useState, useEffect } from "react";
// import Cookies from "js-cookie";
// import { useNavigate } from "react-router-dom";
// import { toast } from "react-toastify";
// import "react-toastify/dist/ReactToastify.css";

// const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

// // Date formatting helper function
// const formatDate = (dateString) => {
//   if (!dateString) return "";

//   const date = new Date(dateString);
//   if (isNaN(date.getTime())) return dateString; // Return original if invalid date

//   const day = String(date.getDate()).padStart(2, "0");
//   const month = String(date.getMonth() + 1).padStart(2, "0");
//   const year = date.getFullYear();

//   return `${day}-${month}-${year}`;
// };

// // Helper function to get today's date in YYYY-MM-DD format
// const getTodayDate = () => {
//   const today = new Date();
//   const year = today.getFullYear();
//   const month = String(today.getMonth() + 1).padStart(2, "0");
//   const day = String(today.getDate()).padStart(2, "0");
//   return `${year}-${month}-${day}`;
// };

// const CheckoutDetails = () => {
//   const [reservations, setReservations] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [guestData, setGuestData] = useState(null);
//   const [showDisclaimer, setShowDisclaimer] = useState(false);
//   const [disclaimerData, setDisclaimerData] = useState(null);
//   const [isLoading, setIsLoading] = useState(false);
//   const [selectedReservation, setSelectedReservation] = useState(null);
//   const navigate = useNavigate();

//   // Get guest ID from JWT token
//   const getGuestIdFromToken = (token) => {
//     try {
//       const payload = JSON.parse(atob(token.split(".")[1]));
//       return payload.sub;
//     } catch (e) {
//       console.error("Error parsing token:", e);
//       return null;
//     }
//   };

//   // Decrypt user data
//   const decryptUserData = async (encryptedData) => {
//     try {
//       if (encryptedData.email && encryptedData.email.length < 50) {
//         return encryptedData;
//       }

//       const accessToken = Cookies.get("access_token");
//       if (!accessToken) {
//         throw new Error("No access token found");
//       }

//       const response = await fetch(`${BQ_BASE_URL}/bq/api/decrypt-fields`, {
//         method: "POST",
//         headers: {
//           "Content-Type": "application/json",
//           Authorization: `Bearer ${accessToken}`,
//         },
//         body: JSON.stringify({
//           encrypted_fields: {
//             email: encryptedData.email,
//             phone_number: encryptedData.phone_number,
//             country_code: encryptedData.country_code,
//           },
//         }),
//       });

//       if (response.status === 401) {
//         return encryptedData;
//       }

//       if (!response.ok) {
//         throw new Error(`Decryption failed: ${response.statusText}`);
//       }

//       return await response.json();
//     } catch (error) {
//       console.error("Decryption error:", error);
//       return encryptedData;
//     }
//   };

//   // Fetch guest data
//   const getGuestData = async () => {
//     const accessToken = Cookies.get("access_token");
//     if (!accessToken) return null;

//     try {
//       const guestId =
//         Cookies.get("guest_id") || getGuestIdFromToken(accessToken);
//       if (!guestId) return null;

//       const profileResponse = await fetch(
//         `${BQ_BASE_URL}/bq/api/profile?guest_id=${guestId}`,
//         {
//           method: "GET",
//           headers: {
//             Accept: "application/json",
//             Authorization: `Bearer ${accessToken}`,
//           },
//         }
//       );

//       if (!profileResponse.ok) {
//         throw new Error(`Profile API Error: ${profileResponse.statusText}`);
//       }

//       const profileData = await profileResponse.json();
//       const isDecrypted =
//         profileData.emailid && profileData.emailid.length < 50;

//       if (isDecrypted) {
//         return {
//           firstname: profileData.firstname || "",
//           lastname: profileData.lastname || "",
//           email: profileData.emailid || "",
//         };
//       }

//       const decryptedFields = await decryptUserData({
//         email: profileData.emailid,
//         phone_number: profileData.phonenumber,
//         country_code: profileData.countrycode,
//       });

//       return {
//         firstname: profileData.firstname || "",
//         lastname: profileData.lastname || "",
//         email: decryptedFields.email || profileData.emailid || "",
//       };
//     } catch (error) {
//       console.error("Error fetching guest data:", error);
//       return null;
//     }
//   };

//   // Fetch valid checkout list
//   const fetchCheckoutValidList = async (email) => {
//     try {
//       const accessToken = Cookies.get("access_token");
//       if (!accessToken) {
//         throw new Error("No access token found");
//       }

//       const response = await fetch(
//         `${BQ_BASE_URL}/bq/api/checkout-valid-list?email=${encodeURIComponent(
//           email
//         )}`,
//         {
//           method: "GET",
//           headers: {
//             Accept: "application/json",
//             Authorization: `Bearer ${accessToken}`,
//           },
//         }
//       );

//       if (!response.ok) {
//         throw new Error(
//           `Checkout valid list API Error: ${response.statusText}`
//         );
//       }

//       const data = await response.json();
//       return data.reservations || [];
//     } catch (error) {
//       console.error("Error fetching checkout valid list:", error);
//       throw error;
//     }
//   };

//   // Calculate checkout status
//   const calculateCheckoutStatus = (
//     checkoutDate,
//     isEarlyCheckout,
//     isLateCheckout
//   ) => {
//     // Ensure we have a valid date
//     if (!checkoutDate) {
//       return {
//         type: "unknown",
//         status: "error",
//         message: "Checkout date not available",
//         chargeApplied: false,
//       };
//     }

//     const now = new Date();
//     let checkoutDateTime;

//     // Handle different date formats from API
//     if (typeof checkoutDate === "string") {
//       checkoutDateTime = new Date(checkoutDate);
//     } else if (checkoutDate instanceof Date) {
//       checkoutDateTime = new Date(checkoutDate);
//     } else {
//       // If it's an object with date properties
//       checkoutDateTime = new Date(checkoutDate.date || checkoutDate);
//     }

//     // Validate the date
//     if (isNaN(checkoutDateTime.getTime())) {
//       return {
//         type: "unknown",
//         status: "error",
//         message: "Invalid checkout date format",
//         chargeApplied: false,
//       };
//     }

//     // Set checkout time to 1 PM (hotel standard checkout time)
//     checkoutDateTime.setHours(11, 0, 0, 0);

//     // Check if it's early checkout (before scheduled checkout day)
//     const isBeforeScheduledDay =
//       now < new Date(checkoutDateTime.setHours(0, 0, 0, 0));

//     if (isEarlyCheckout || isBeforeScheduledDay) {
//       // Early checkout (before scheduled checkout day)
//       if (now < checkoutDateTime) {
//         return {
//           type: "early",
//           status: "no_charge",
//           message: "Early checkout before 1 PM - No additional charge",
//           chargeApplied: false,
//         };
//       } else {
//         return {
//           type: "early",
//           status: "extra_day_charge",
//           message: "Early checkout after 1 PM - Charged for an extra day",
//           chargeApplied: true,
//         };
//       }
//     }

//     // Check if it's late checkout (on scheduled checkout day)
//     if (isLateCheckout || !isBeforeScheduledDay) {
//       const gracePeriodEnd = new Date(checkoutDateTime);
//       gracePeriodEnd.setHours(15, 0, 0, 0); // 3 PM grace period end

//       if (now <= gracePeriodEnd) {
//         return {
//           type: "late",
//           status: "grace_period",
//           message:
//             "Late checkout within grace period (until 3 PM) - No additional charge",
//           chargeApplied: false,
//         };
//       } else {
//         return {
//           type: "late",
//           status: "extra_day_charge",
//           message: "Late checkout after 3 PM - Charged for an extra day",
//           chargeApplied: true,
//         };
//       }
//     }

//     // Default case (shouldn't normally reach here)
//     return {
//       type: "normal",
//       status: "on_time",
//       message: "Checkout on time - No additional charge",
//       chargeApplied: false,
//     };
//   };

//   // Initialize data
//   useEffect(() => {
//     const initializeData = async () => {
//       try {
//         setLoading(true);
//         const guest = await getGuestData();
//         if (!guest) {
//           throw new Error("Failed to fetch guest data");
//         }
//         setGuestData(guest);

//         const reservations = await fetchCheckoutValidList(guest.email);
//         setReservations(reservations);
//       } catch (err) {
//         setError(err.message);
//         toast.error(err.message, {
//           position: "top-center",
//           autoClose: 5000,
//           hideProgressBar: false,
//           closeOnClick: true,
//           pauseOnHover: true,
//           draggable: true,
//           progress: undefined,
//         });
//       } finally {
//         setLoading(false);
//       }
//     };

//     initializeData();
//   }, []);

//   const handleProceed = (bookingId, orderId, email, reservation) => {
//     // Get today's date for desired_checkout_date
//     const desiredCheckoutDate = getTodayDate();

//     // Calculate checkout status
//     const checkoutStatus = calculateCheckoutStatus(
//       reservation.reservation_details.checkout_date,
//       false, // isEarlyCheckout - you might need to adjust this based on your data
//       false  // isLateCheckout - you might need to adjust this based on your data
//     );

//     // Show disclaimer if there's a charge or it's an early checkout
//     if (checkoutStatus.chargeApplied || checkoutStatus.type === "early") {
//       setSelectedReservation({
//         bookingId,
//         orderId,
//         email,
//         reservation,
//         desiredCheckoutDate
//       });
//       setDisclaimerData({
//         checkoutStatus,
//         reservationDetails: reservation.reservation_details
//       });
//       setShowDisclaimer(true);
//     } else {
//       // Proceed directly if no disclaimer needed
//       proceedToCheckoutVerification(bookingId, orderId, email, desiredCheckoutDate);
//     }
//   };

//   const handleDisclaimerContinue = () => {
//     if (selectedReservation) {
//       proceedToCheckoutVerification(
//         selectedReservation.bookingId,
//         selectedReservation.orderId,
//         selectedReservation.email,
//         selectedReservation.desiredCheckoutDate
//       );
//     }
//     setShowDisclaimer(false);
//   };

//   const handleDisclaimerCancel = () => {
//     setShowDisclaimer(false);
//     setSelectedReservation(null);
//     setDisclaimerData(null);
//   };

//   const proceedToCheckoutVerification = async (bookingId, orderId, email, desiredCheckoutDate) => {
//     try {
//       setIsLoading(true);
//       const accessToken = Cookies.get("access_token");
//       if (!accessToken) {
//         throw new Error("No access token found");
//       }

//       // Build URL with desired_checkout_date parameter
//       const url = `${BQ_BASE_URL}/bq/api/check-out-verification?orderid=${orderId}&email=${encodeURIComponent(
//         email
//       )}&desired_checkout_date=${desiredCheckoutDate}`;

//       // Call the checkout verification API (GET)
//       const response = await fetch(url, {
//         method: "GET",
//         headers: {
//           accept: "application/json",
//           Authorization: `Bearer ${accessToken}`,
//         },
//       });

//       if (!response.ok) {
//         const errorData = await response.json();
//         if (errorData.detail) {
//           const { message, details } = errorData.detail;
//           let errorMessage = message;

//           if (details) {
//             errorMessage += ` (${details.code})`;
//             if (details.resolution) {
//               errorMessage += ` - ${details.resolution}`;
//             }
//           }

//           throw new Error(errorMessage);
//         }
//         throw new Error(`Checkout verification failed: ${response.statusText}`);
//       }

//       const data = await response.json();

//       if (data.success) {
//         // After successful verification, call the POST API
//         await proceedToCheckout(bookingId, orderId, email, desiredCheckoutDate, data);
//       } else {
//         throw new Error(data.message || "Checkout verification failed");
//       }
//     } catch (error) {
//       console.error("Error during checkout verification:", error);
//       toast.error(error.message, {
//         position: "top-center",
//         autoClose: 5000,
//         hideProgressBar: false,
//         closeOnClick: true,
//         pauseOnHover: true,
//         draggable: true,
//         progress: undefined,
//       });
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   const proceedToCheckout = async (bookingId, orderId, email, desiredCheckoutDate, verificationData) => {
//     try {
//       setIsLoading(true);
//       const accessToken = Cookies.get("access_token");
//       if (!accessToken) {
//         throw new Error("No access token found");
//       }

//       // Build URL with desired_checkout_date parameter for POST request
//       const url = `${BQ_BASE_URL}/bq/api/check-out-verification?orderid=${orderId}&email=${encodeURIComponent(
//         email
//       )}&desired_checkout_date=${desiredCheckoutDate}`;

//       // Call the checkout POST API
//       const response = await fetch(url, {
//         method: "POST",
//         headers: {
//           accept: "application/json",
//           Authorization: `Bearer ${accessToken}`,
//         },
//         body: JSON.stringify({}),
//       });

//       if (!response.ok) {
//         const errorData = await response.json();
//         if (errorData.detail) {
//           const { message, details } = errorData.detail;
//           let errorMessage = message;

//           if (details) {
//             errorMessage += ` (${details.code})`;
//             if (details.resolution) {
//               errorMessage += ` - ${details.resolution}`;
//             }
//           }

//           throw new Error(errorMessage);
//         }
//         throw new Error(`Checkout failed: ${response.statusText}`);
//       }

//       const data = await response.json();

//       if (data.success) {
//         // Store the checkout data in localStorage
//         localStorage.setItem("checkoutData", JSON.stringify(data));

//         // Store individual values for easy access
//         localStorage.setItem("orderId", data.orderid);
//         localStorage.setItem("bookingId", data.bookingid);
//         localStorage.setItem("earlyCheckout", data.early_checkout);
//         localStorage.setItem(
//           "reservationDetails",
//           JSON.stringify(data.reservation_details)
//         );
//         localStorage.setItem(
//           "primaryGuest",
//           JSON.stringify(data.primary_guest)
//         );

//         // Navigate to booking checkin page
//         navigate("/booking-checkin");
//       } else {
//         throw new Error(data.message || "Checkout failed");
//       }
//     } catch (error) {
//       console.error("Error during checkout:", error);
//       toast.error(error.message, {
//         position: "top-center",
//         autoClose: 5000,
//         hideProgressBar: false,
//         closeOnClick: true,
//         pauseOnHover: true,
//         draggable: true,
//         progress: undefined,
//       });
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   if (loading) {
//     return (
//       <div className="min-h-screen bg-gray-50 flex items-center justify-center">
//         <div className="text-2xl font-semibold text-gray-800">Loading...</div>
//       </div>
//     );
//   }

//   if (error) {
//     return (
//       <div className="min-h-screen bg-gray-50 flex items-center justify-center">
//         <div className="text-2xl font-semibold text-red-600">{error}</div>
//       </div>
//     );
//   }

//   return (
//     <div className="min-h-1 mb-44 pb-24">
//       {/* Main Content */}
//       <main className=" mx-auto ">
//         {reservations.length === 0 ? (
//           <div className="flex justify-center mt-40 ">
//             <p className="text-black font-bold text-2xl">
//               No reservations available for checkout
//             </p>
//           </div>
//         ) : (
//           <div className="bg-white shadow-sm rounded-lg overflow-hidden  ">
//             <div className="overflow-x-auto ">
//               <table className="min-w-full divide-y divide-gray-200 ">
//                 <thead className="bg-gray-50">
//                   <tr>
//                     <th
//                       scope="col"
//                       className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
//                     >
//                       Room Details
//                     </th>
//                     <th
//                       scope="col"
//                       className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
//                     >
//                       Check-in
//                     </th>
//                     <th
//                       scope="col"
//                       className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
//                     >
//                       Check-out
//                     </th>
//                     <th
//                       scope="col"
//                       className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
//                     >
//                       Nights
//                     </th>
//                     <th
//                       scope="col"
//                       className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
//                     >
//                       Guests
//                     </th>
//                     <th
//                       scope="col"
//                       className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
//                     >
//                       Action
//                     </th>
//                   </tr>
//                 </thead>
//                 <tbody className="bg-white divide-y divide-gray-200">
//                   {reservations.map((reservation) => (
//                     <tr
//                       key={reservation.bookingid}
//                       className="hover:bg-gray-50"
//                     >
//                       <td className="px-3 py-4 whitespace-nowrap">
//                         <div className="text-sm font-medium text-gray-900">
//                           Room {reservation.reservation_details.room_number}
//                         </div>
//                         <div className="text-sm text-gray-500">
//                           {reservation.reservation_details.room_type}
//                         </div>
//                       </td>
//                       <td className="px-3 py-4 whitespace-nowrap">
//                         <div className="text-sm text-gray-900">
//                           {formatDate(
//                             reservation.reservation_details.checkin_date
//                           )}
//                         </div>
//                       </td>
//                       <td className="px-3 py-4 whitespace-nowrap">
//                         <div className="text-sm text-gray-900">
//                           {formatDate(
//                             reservation.reservation_details.checkout_date
//                           )}
//                         </div>
//                       </td>
//                       <td className="px-3 py-4 whitespace-nowrap">
//                         <div className="text-sm text-gray-900">
//                           {reservation.reservation_details.length_of_stay}
//                         </div>
//                       </td>
//                       <td className="px-3 py-4 whitespace-nowrap">
//                         <div className="text-sm text-gray-900">
//                           {reservation.reservation_details.number_of_guests}
//                         </div>
//                       </td>
//                       <td className="px-3 py-4 whitespace-nowrap text-right text-sm font-medium">
//                         <button
//                           onClick={() =>
//                             handleProceed(
//                               reservation.bookingid,
//                               reservation.orderid,
//                               reservation.primary_guest.emailid,
//                               reservation
//                             )
//                           }
//                           className="bg-black text-white px-4 py-2 rounded hover:bg-gray-800 text-xs"
//                         >
//                           Proceed to Checkout
//                         </button>
//                       </td>
//                     </tr>
//                   ))}
//                 </tbody>
//               </table>
//             </div>
//           </div>
//         )}
//       </main>

//       {/* Disclaimer Popup */}
//       {showDisclaimer && disclaimerData && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//           <div className="bg-white rounded-lg max-w-lg w-full p-7">
//             <h2 className="text-xl font-bold mb-4">
//               {disclaimerData.checkoutStatus.type === "early"
//                 ? "Early Checkout Notice"
//                 : "Late Checkout Notice"}
//             </h2>

//             {disclaimerData.checkoutStatus.type === "early" &&
//               !disclaimerData.checkoutStatus.chargeApplied && (
//                 <p className="mb-4">
//                   You are trying to check out earlier than your scheduled
//                   check-out date of{" "}
//                   <strong>
//                     {formatDate(
//                       disclaimerData.reservationDetails.checkout_date
//                     )}
//                   </strong>
//                   . Are you sure you want to continue?
//                 </p>
//               )}

//             {disclaimerData.checkoutStatus.type === "early" &&
//               disclaimerData.checkoutStatus.chargeApplied && (
//                 <p className="mb-4">
//                   You are checking out early instead of your scheduled check-out
//                   on{" "}
//                   <strong>
//                     {formatDate(
//                       disclaimerData.reservationDetails.checkout_date
//                     )}
//                   </strong>
//                   . Since you are leaving after 1 PM, it will be considered as
//                   an additional day, and you will incur a charge of one extra
//                   day .
//                 </p>
//               )}

//             {disclaimerData.checkoutStatus.type === "late" &&
//               !disclaimerData.checkoutStatus.chargeApplied && (
//                 <p className="mb-4">
//                   You are checking out late but within the grace period (until 3
//                   PM). No additional charges will be applied.
//                 </p>
//               )}

//             {disclaimerData.checkoutStatus.type === "late" &&
//               disclaimerData.checkoutStatus.chargeApplied && (
//                 <p className="mb-4">
//                   Since your check-out was after <strong>3:00 PM</strong>, an
//                   additional charge equivalent to one extra day has been applied
//                   .
//                 </p>
//               )}

//             <div className="flex justify-end space-x-4 mt-10">
//               <button
//                 onClick={handleDisclaimerCancel}
//                 className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100"
//               >
//                 Cancel
//               </button>
//               <button
//                 onClick={handleDisclaimerContinue}
//                 disabled={isLoading}
//                 className={`px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 ${
//                   isLoading ? "opacity-50 cursor-not-allowed" : ""
//                 }`}
//               >
//                 {isLoading ? "Processing..." : "Proceed"}
//               </button>
//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default CheckoutDetails;
